import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { AxiosResponse } from "axios";

// Types come from the server schemas — single source of truth.
import type {
  GetConversationsRequest,
  GetConversationsResponse,
  GetUnreadSummaryRequest,
  GetUnreadSummaryResponse,
  CreateConversationRequest,
  CreateConversationResponse,
  GetMessagesRequest,
  GetMessagesResponse,
  SendMessageRequest,
  SendMessageResponse,
  EditMessageRequest,
  EditMessageResponse,
  DeleteMessageRequest,
  DeleteMessageResponse,
  DeleteAttachmentRequest,
  DeleteAttachmentResponse,
  ToggleReactionRequest,
  ToggleReactionResponse,
  GetMessageReceiptsRequest,
  GetMessageReceiptsResponse,
  MarkConversationReadRequest,
  MarkConversationReadResponse,
  RenameConversationRequest,
  RenameConversationResponse,
  AddMembersRequest,
  AddMembersResponse,
  RemoveMemberRequest,
  RemoveMemberResponse,
  UpdateConversationStateRequest,
  UpdateConversationStateResponse,
  CreateSectionRequest,
  CreateSectionResponse,
  RenameSectionRequest,
  RenameSectionResponse,
  DeleteSectionRequest,
  DeleteSectionResponse,
  ReorderSectionsRequest,
  ReorderSectionsResponse,
  UpdateProjectOrderRequest,
  UpdateProjectOrderResponse,
  GetConversationAttachmentsRequest,
  GetConversationAttachmentsResponse,
  GetChatDirectoryRequest,
  GetChatDirectoryResponse,
} from "@ServerTypes/portalChat/portalChat.schema";

const url = (path: string) => `${getBackendUrl()}/portalChat/${path}`;

export const getConversationsRequest = (
  data: GetConversationsRequest = {},
): Promise<AxiosResponse<GetConversationsResponse>> =>
  axiosWrapper.post<GetConversationsResponse, GetConversationsRequest>(url("getConversations"), data);

export const getUnreadSummaryRequest = (
  data: GetUnreadSummaryRequest = {},
): Promise<AxiosResponse<GetUnreadSummaryResponse>> =>
  axiosWrapper.post<GetUnreadSummaryResponse, GetUnreadSummaryRequest>(url("getUnreadSummary"), data);

export const createConversationRequest = (
  data: CreateConversationRequest,
): Promise<AxiosResponse<CreateConversationResponse>> =>
  axiosWrapper.post<CreateConversationResponse, CreateConversationRequest>(url("createConversation"), data);

export const getMessagesRequest = (data: GetMessagesRequest): Promise<AxiosResponse<GetMessagesResponse>> =>
  axiosWrapper.post<GetMessagesResponse, GetMessagesRequest>(url("getMessages"), data);

export const sendMessageRequest = (data: SendMessageRequest): Promise<AxiosResponse<SendMessageResponse>> =>
  axiosWrapper.post<SendMessageResponse, SendMessageRequest>(url("sendMessage"), data);

export const editMessageRequest = (data: EditMessageRequest): Promise<AxiosResponse<EditMessageResponse>> =>
  axiosWrapper.post<EditMessageResponse, EditMessageRequest>(url("editMessage"), data);

export const deleteMessageRequest = (data: DeleteMessageRequest): Promise<AxiosResponse<DeleteMessageResponse>> =>
  axiosWrapper.post<DeleteMessageResponse, DeleteMessageRequest>(url("deleteMessage"), data);

export const deleteAttachmentRequest = (
  data: DeleteAttachmentRequest,
): Promise<AxiosResponse<DeleteAttachmentResponse>> =>
  axiosWrapper.post<DeleteAttachmentResponse, DeleteAttachmentRequest>(url("deleteAttachment"), data);

export const toggleReactionRequest = (data: ToggleReactionRequest): Promise<AxiosResponse<ToggleReactionResponse>> =>
  axiosWrapper.post<ToggleReactionResponse, ToggleReactionRequest>(url("toggleReaction"), data);

export const getMessageReceiptsRequest = (
  data: GetMessageReceiptsRequest,
): Promise<AxiosResponse<GetMessageReceiptsResponse>> =>
  axiosWrapper.post<GetMessageReceiptsResponse, GetMessageReceiptsRequest>(url("getMessageReceipts"), data);

export const markConversationReadRequest = (
  data: MarkConversationReadRequest,
): Promise<AxiosResponse<MarkConversationReadResponse>> =>
  axiosWrapper.post<MarkConversationReadResponse, MarkConversationReadRequest>(url("markConversationRead"), data);

export const renameConversationRequest = (
  data: RenameConversationRequest,
): Promise<AxiosResponse<RenameConversationResponse>> =>
  axiosWrapper.post<RenameConversationResponse, RenameConversationRequest>(url("renameConversation"), data);

export const addMembersRequest = (data: AddMembersRequest): Promise<AxiosResponse<AddMembersResponse>> =>
  axiosWrapper.post<AddMembersResponse, AddMembersRequest>(url("addMembers"), data);

export const removeMemberRequest = (data: RemoveMemberRequest): Promise<AxiosResponse<RemoveMemberResponse>> =>
  axiosWrapper.post<RemoveMemberResponse, RemoveMemberRequest>(url("removeMember"), data);

export const updateConversationStateRequest = (
  data: UpdateConversationStateRequest,
): Promise<AxiosResponse<UpdateConversationStateResponse>> =>
  axiosWrapper.post<UpdateConversationStateResponse, UpdateConversationStateRequest>(
    url("updateConversationState"),
    data,
  );

export const createSectionRequest = (data: CreateSectionRequest): Promise<AxiosResponse<CreateSectionResponse>> =>
  axiosWrapper.post<CreateSectionResponse, CreateSectionRequest>(url("createSection"), data);

export const renameSectionRequest = (data: RenameSectionRequest): Promise<AxiosResponse<RenameSectionResponse>> =>
  axiosWrapper.post<RenameSectionResponse, RenameSectionRequest>(url("renameSection"), data);

export const deleteSectionRequest = (data: DeleteSectionRequest): Promise<AxiosResponse<DeleteSectionResponse>> =>
  axiosWrapper.post<DeleteSectionResponse, DeleteSectionRequest>(url("deleteSection"), data);

export const reorderSectionsRequest = (data: ReorderSectionsRequest): Promise<AxiosResponse<ReorderSectionsResponse>> =>
  axiosWrapper.post<ReorderSectionsResponse, ReorderSectionsRequest>(url("reorderSections"), data);

export const updateProjectOrderRequest = (
  data: UpdateProjectOrderRequest,
): Promise<AxiosResponse<UpdateProjectOrderResponse>> =>
  axiosWrapper.post<UpdateProjectOrderResponse, UpdateProjectOrderRequest>(url("updateProjectOrder"), data);

export const getConversationAttachmentsRequest = (
  data: GetConversationAttachmentsRequest,
): Promise<AxiosResponse<GetConversationAttachmentsResponse>> =>
  axiosWrapper.post<GetConversationAttachmentsResponse, GetConversationAttachmentsRequest>(
    url("getConversationAttachments"),
    data,
  );

export const getChatDirectoryRequest = (
  data: GetChatDirectoryRequest = {},
): Promise<AxiosResponse<GetChatDirectoryResponse>> =>
  axiosWrapper.post<GetChatDirectoryResponse, GetChatDirectoryRequest>(url("getChatDirectory"), data);

// Convenience element types for components. Every response is a discriminated union on `status`, so
// the payload members live on the success arm only — `Extract<..., { status: true }>` selects it.
type GetConversationsSuccess = Extract<GetConversationsResponse, { status: true }>;
type GetMessagesSuccess = Extract<GetMessagesResponse, { status: true }>;
type GetChatDirectorySuccess = Extract<GetChatDirectoryResponse, { status: true }>;
type GetConversationAttachmentsSuccess = Extract<GetConversationAttachmentsResponse, { status: true }>;
type GetMessageReceiptsSuccess = Extract<GetMessageReceiptsResponse, { status: true }>;

export type ChatConversation = NonNullable<GetConversationsSuccess["result"]> extends (infer T)[] ? T : never;
export type ChatSection = NonNullable<GetConversationsSuccess["sections"]> extends (infer T)[] ? T : never;
export type ChatMessage = NonNullable<GetMessagesSuccess["result"]> extends (infer T)[] ? T : never;
export type ChatMember = NonNullable<GetMessagesSuccess["members"]> extends (infer T)[] ? T : never;
export type ChatDirectoryUser = NonNullable<GetChatDirectorySuccess["result"]> extends (infer T)[] ? T : never;
export type ChatAttachmentItem =
  NonNullable<GetConversationAttachmentsSuccess["result"]> extends (infer T)[] ? T : never;
export type GetMessageReceiptsResponseData = {
  message?: GetMessageReceiptsSuccess["message"];
  readBy: NonNullable<GetMessageReceiptsSuccess["readBy"]>;
  unreadBy: NonNullable<GetMessageReceiptsSuccess["unreadBy"]>;
};
